// Terrain shadows on the Moon, per pixel.
//
// A Moon terrain patch keeps three height maps of the LOLA ground around its
// centre, laid out on the patch's own tangent grid (the (x, z) the rings are
// built on, arc lengths from the centre), 512 texels each: near over +-42.7 km
// (167 m, the finest LOLA posts), mid over +-200 km (780 m) and far over
// +-900 km (3.5 km).
// Heights are metres above the 1737.4 km sphere, in half floats so they
// filter linearly everywhere.
//
// The shader marches from each pixel toward the sun through the maps, in the
// patch's cartesian frame (so the Moon's curvature is exact), and compares the
// ray's radius with the ground's. The penumbra is the sun's own disc: a ray
// grazing ground that rises within half a degree of it is partly lit, which
// gives crisp shadows near their casters and soft ones kilometres away, as
// under a real sun. A caster a coarser map cannot resolve widens the penumbra
// by its texel, so distant shadows go soft instead of polygonal. Craters
// smaller than the maps shadow themselves in moonmaterial.js.
import * as THREE from 'three';
import { moonDemHeight } from './moondem.js';

export const HZ_SIZE = 512;
export const HZ_NEAR_HALF = 256 * 166.75;
export const HZ_MID_HALF = 200e3;
export const HZ_FAR_HALF = 900e3;

export function createHorizonMaps() {
  const make = () => {
    const t = new THREE.DataTexture(new Uint16Array(HZ_SIZE * HZ_SIZE), HZ_SIZE, HZ_SIZE, THREE.RedFormat, THREE.HalfFloatType);
    t.minFilter = t.magFilter = THREE.LinearFilter;
    t.wrapS = t.wrapT = THREE.ClampToEdgeWrapping;
    t.generateMipmaps = false;
    t.needsUpdate = true;
    return t;
  };
  return { near: make(), mid: make(), far: make(), ready: false };
}

const _d = new THREE.Vector3();

// Fill both maps for a patch centred on `basis` (terrain.js siteBasis), a row
// per step. `dirAt(basis, R, x, z, out)` is terrain.js's grid mapping.
export function* horizonMapSteps(maps, basis, R, dirAt) {
  const near = new Uint16Array(HZ_SIZE * HZ_SIZE), mid = new Uint16Array(HZ_SIZE * HZ_SIZE), far = new Uint16Array(HZ_SIZE * HZ_SIZE);
  for (const [data, half] of [[near, HZ_NEAR_HALF], [mid, HZ_MID_HALF], [far, HZ_FAR_HALF]]) {
    const cell = (2 * half) / HZ_SIZE;
    for (let j = 0; j < HZ_SIZE; j++) {
      // Texel centres; row 0 at z = -half (south is +z), matching the shader.
      const z = -half + (j + 0.5) * cell;
      for (let i = 0; i < HZ_SIZE; i++) {
        const x = -half + (i + 0.5) * cell;
        dirAt(basis, R, x, z, _d);
        const h = moonDemHeight(Math.asin(THREE.MathUtils.clamp(_d.y, -1, 1)), Math.atan2(-_d.z, _d.x), cell * 1.5, false).h;
        data[j * HZ_SIZE + i] = THREE.DataUtils.toHalfFloat(h);
      }
      if (j % 8 === 7) yield;
    }
  }
  maps.near.image.data.set(near); maps.near.needsUpdate = true;
  maps.mid.image.data.set(mid); maps.mid.needsUpdate = true;
  maps.far.image.data.set(far); maps.far.needsUpdate = true;
  maps.ready = true;
}

const NH = HZ_NEAR_HALF, MH = HZ_MID_HALF, FH = HZ_FAR_HALF;
export const HORIZON_GLSL = /* glsl */`
uniform sampler2D uHzNear;
uniform sampler2D uHzMid;
uniform sampler2D uHzFar;
uniform float uHzReady;
uniform float uPatchH;
// Ground height (m above the sphere) at patch-local cartesian point p; the
// patch centre sits at radius uPatchRc on the local +y axis. r is returned as
// the point's own height above the sphere, computed without subtracting two
// 1.7e6 m radii (float32 would leave 0.2 m of noise, which reads as a
// shimmering half-shadow a few metres along the ray); texel is the map's
// resolution there.
float hzGround(vec3 p, out float r, out float texel) {
  vec3 q = vec3(p.x, p.y + uPatchRc, p.z);
  float ql = length(q);
  r = (dot(p, p) + 2.0 * p.y * uPatchRc + uPatchH * (uPatchRc + uMoonR)) / (ql + uMoonR);
  float horiz = length(q.xz);
  float arc = atan(horiz, q.y) * uMoonR;
  vec2 g = horiz > 1e-3 ? q.xz * (arc / horiz) : vec2(0.0);
  float m = max(abs(g.x), abs(g.y));
  texel = ${(2 * NH / 512).toFixed(3)};
  if (m < ${(NH * 0.97).toFixed(1)}) return texture2D(uHzNear, g / ${(2 * NH).toFixed(1)} + 0.5).r;
  texel = ${(2 * MH / 512).toFixed(3)};
  if (m < ${(MH * 0.98).toFixed(1)}) return texture2D(uHzMid, g / ${(2 * MH).toFixed(1)} + 0.5).r;
  texel = ${(2 * FH / 512).toFixed(3)};
  if (m < ${(FH * 0.99).toFixed(1)}) return texture2D(uHzFar, g / ${(2 * FH).toFixed(1)} + 0.5).r;
  return -1e5;
}
// 0..1 visibility of the sun (unit direction sunL, patch-local) from p.
float hzSun(vec3 p, vec3 sunL) {
  if (uHzReady < 0.5 || sunL.y < -0.2) return 1.0;
  float r0, tx0;
  float g0 = hzGround(p, r0, tx0);
  // Start on the LOLA ground, not in a mesh crater below it.
  vec3 up0 = normalize(vec3(p.x, p.y + uPatchRc, p.z));
  float lift = max(0.0, g0 - r0) + 2.0;
  vec3 o = p + up0 * lift;
  const float SUN = 0.00466;       // the sun's angular radius (rad)
  float vis = 1.0;
  // Steps grow 27 % each, from a per-pixel jittered start so their spacing
  // shows as fine grain in far penumbrae rather than as saw teeth.
  float jitter = fract(52.9829189 * fract(dot(gl_FragCoord.xy, vec2(0.06711056, 0.00583715))));
  float t = 20.0 * (1.0 + 0.27 * jitter);
  for (int i = 0; i < 44; i++) {
    vec3 q = o + sunL * t;
    float r, texel;
    float g = hzGround(q, r, texel);
    if (g < -9e4) break;
    // Within two texels of the pixel the map only repeats the slope the mesh
    // already lights (dotNL); casters start beyond that.
    if (t < 2.0 * texel) { t *= 1.27; continue; }
    // Height of the ground over the ray, as an angle seen from the pixel,
    // against the sun's disc widened by what this map cannot resolve.
    float over = (g - r) / t;
    float width = 2.0 * SUN + 0.15 * texel / t;
    vis = min(vis, clamp(0.5 - over / width, 0.0, 1.0));
    if (vis <= 0.0 || r > 11000.0) break;
    t *= 1.27;
  }
  return vis;
}
`;
